import { gql } from '@apollo/client';
import {
  GetProjectsParams,
  ProjectSlimForList,
} from 'spw-redux/modules/projects/types';
import {
  TeamSlimForList,
  Team,
  UpdateTeamFields,
  TeamSlim,
  TeamsSortParams,
  GetUsersInTeamParams,
  TeamSortOptions,
} from 'spw-redux/modules/teams/types';
import { IUser } from 'spw-redux/modules/user/types';
import { graphqlClient } from './utils';

export const getTeam = (id: number) =>
  graphqlClient.query<{ team: Team }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetTeam($teamId: ID!) {
        team: getTeam(teamId: $teamId) {
          id
          name
          hasLogo
          thumbnailS3Path
          requiresRequestToJoin
          currentUserRole
          teamAdmins {
            id
            name
            hasAvatar
            thumbnailS3Path
            isActive
            professionalRole
          }
          numberOfProjects
          numberOfTeamMembers
          users {
            id
          }
        }
      }
    `,
    variables: { teamId: id },
  });

export const getAllTeams = (params: TeamsSortParams) =>
  graphqlClient.query<{ results: { total: number; teams: TeamSlimForList[] } }>(
    {
      fetchPolicy: 'no-cache',
      query: gql`
        query GetAllTeams($page: Int, $perPage: Int, $sort: TeamSortOptions) {
          results: getTeams(page: $page, perPage: $perPage, sort: $sort) {
            total
            teams {
              id
              name
              currentUserRole
              hasLogo
              thumbnailS3Path
              requiresRequestToJoin
              teamAdmins {
                id
                name
              }
              numberOfProjects
              numberOfTeamMembers
              users {
                id
                name
                numberOfProjects
                numberOfTeams
                officeLocation
                professionalRole
              }
            }
          }
        }
      `,
      variables: params,
    }
  );

// TODO: update query to search by team name once available
export const getTeamsSuggestions = () =>
  graphqlClient.query<{ teams: { teams: TeamSlim[] } }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetTeamsSuggestions {
        teams: getTeams(page: 1, perPage: 999) {
          teams {
            id
            name
            hasLogo
            thumbnailS3Path
          }
        }
      }
    `,
  });

export const getTeamsForPopoverGivenUser = (userId: number) =>
  graphqlClient.query<{ teams: { teams: TeamSlim[] } }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetTeamsForPopoverGivenUser($userId: ID) {
        teams: getTeams(
          page: 1
          perPage: 999
          sort: { name: ASC }
          filter: { userId: $userId }
        ) {
          teams {
            id
            name
            hasLogo
            thumbnailS3Path
          }
        }
      }
    `,
    variables: { userId },
  });

export const getTeamsForPopoverGivenClient = (clientId: number) =>
  graphqlClient.query<{ teams: TeamSlim[] }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetTeamsForPopoverGivenClient($clientId: ID) {
        teams: getTeams(
          perPage: 1000
          sort: { name: ASC }
          filter: { clientId: $clientId }
        ) {
          teams {
            id
            name
            hasLogo
            thumbnailS3Path
          }
        }
      }
    `,
    variables: { clientId },
  });

export const createTeam = (params: {
  name: string;
  teamAdmins: number[];
  requiresRequestToJoin: boolean;
}) =>
  graphqlClient.mutate<{ team: { id: number } }>({
    mutation: gql`
      mutation CreateTeam(
        $name: String!
        $teamAdmins: [ID]
        $requiresRequestToJoin: Boolean
      ) {
        team: createTeam(
          name: $name
          teamAdmins: $teamAdmins
          requiresRequestToJoin: $requiresRequestToJoin
        ) {
          id
        }
      }
    `,
    variables: params,
  });

export const updateTeam = (params: UpdateTeamFields) =>
  graphqlClient.mutate<{ team: Team }>({
    mutation: gql`
      mutation updateTeam(
        $teamId: Int!
        $name: String
        $image: String
        $requiresRequestToJoin: Boolean
        $teamAdmins: [ID]
      ) {
        team: updateTeam(
          teamId: $teamId
          name: $name
          image: $image
          requiresRequestToJoin: $requiresRequestToJoin
          teamAdmins: $teamAdmins
        ) {
          id
          name
          hasLogo
          thumbnailS3Path
          requiresRequestToJoin
          currentUserRole
          teamAdmins {
            id
            name
            hasAvatar
            thumbnailS3Path
          }
          numberOfProjects
          numberOfTeamMembers
          users {
            id
          }
        }
      }
    `,
    variables: params,
  });

export const deleteTeam = (id: number) =>
  graphqlClient.query<{ success: boolean }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation DeleteTeam($teamId: Int!) {
        success: deleteTeam(teamId: $teamId)
      }
    `,
    variables: { teamId: id },
  });

export const joinTeam = (id: number) =>
  graphqlClient.query<{ success: boolean }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation JoinTeam($teamId: Int!) {
        success: joinTeam(teamId: $teamId)
      }
    `,
    variables: { teamId: id },
  });

export const issueRequestToJoinTeam = (id: number) =>
  graphqlClient.query<{ issueRequestToJoinTeam: { id: number } }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation IssueRequestToJoinTeam($teamId: Int!) {
        issueRequestToJoinTeam: requestToJoinTeam(teamId: $teamId) {
          id
        }
      }
    `,
    variables: { teamId: id },
  });

export const leaveTeam = (id: number) =>
  graphqlClient.query<{ success: boolean }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation LeaveTeam($teamId: Int!) {
        success: leaveTeam(teamId: $teamId)
      }
    `,
    variables: { teamId: id },
  });

export const addUserToTeam = (params: { teamId: number; userId: number }) =>
  graphqlClient.mutate<{ success: boolean }>({
    mutation: gql`
      mutation AddUserToTeam($teamId: Int!, $userId: Int!) {
        success: addUserToTeam(teamId: $teamId, userId: $userId)
      }
    `,
    variables: params,
  });

export const removeUserFromTeam = (params: {
  teamId: number;
  userId: number;
}) =>
  graphqlClient.mutate<{ success: boolean }>({
    mutation: gql`
      mutation RemoveUserFromTeam($teamId: Int!, $userId: Int!) {
        success: removeUserFromTeam(teamId: $teamId, userId: $userId)
      }
    `,
    variables: params,
  });

export const getTeamsForProject = (params: {
  projectId: number;
  page?: number;
  perPage?: number;
  sort?: TeamSortOptions;
}) => {
  return graphqlClient.query<{ results: { total: number; teams: Team[] } }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetTeamsForProject(
        $page: Int
        $perPage: Int
        $sort: TeamSortOptions
        $projectId: ID
      ) {
        results: getTeams(
          page: $page
          perPage: $perPage
          sort: $sort
          filter: { projectId: $projectId }
        ) {
          total
          teams {
            id
            name
            numberOfProjects
            numberOfTeamMembers
            hasLogo
            thumbnailS3Path
            teamAdmins {
              id
              name
            }
            users {
              id
              name
            }
          }
        }
      }
    `,
    variables: params,
  });
};

export const getProjectsForTeam = (
  teamId: number,
  params?: GetProjectsParams
) =>
  graphqlClient.query<{
    results: { total: number; projects: ProjectSlimForList[] };
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetProjectsForTeam(
        $perPage: Int
        $page: Int
        $teamId: Int
        $sort: ProjectSortOptions
      ) {
        results: projects(
          perPage: $perPage
          page: $page
          sort: $sort
          teamId: $teamId
        ) {
          total
          projects {
            id
            insertedAt
            client {
              name
              id
              industry {
                name
              }
            }
            city
            state
            projectName
            projectNumber
            requiresRequestToJoin
            status
            type
            updatedAt
            createdByUser {
              id
              name
              email
            }
            lastEditor {
              id
              name
              isSuperAdmin
            }
            projectOwner {
              id
              name
            }
          }
        }
      }
    `,
    variables: { teamId, ...params },
  });

export const getUsersInTeam = (params: GetUsersInTeamParams) =>
  graphqlClient.query<{ results: { total: number; users: IUser[] } }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetUsersInTeam(
        $page: Int
        $perPage: Int
        $sort: TeamUsersSortOptions
        $teamId: ID!
      ) {
        results: getUsersInTeam(
          page: $page
          perPage: $perPage
          sort: $sort
          teamId: $teamId
        ) {
          total
          users {
            id
            hasAvatar
            thumbnailS3Path
            name
            numberOfProjects
            numberOfTeams
            officeLocation
            professionalRole
          }
        }
      }
    `,
    variables: params,
  });

export const checkTeamExistenceByName = (params: { name: string }) =>
  graphqlClient.query<{ teamExists: boolean }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query CheckTeamExistenceByName($name: String!) {
        teamExists: doesTeamWithParamsExist(name: $name)
      }
    `,
    variables: params,
  });
